import { fareClassToApiCabinMap, FlightsClassTypes } from 'constants/flight'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useContext } from 'react'
import { FareFamiliesResponse, getFlightFareFamilies } from 'api/flights'
import GeoContext from 'contexts/geoContext'
import { mergeOccupants } from 'lib/offer/occupancyUtils'
import { useQuery } from '@tanstack/react-query'
import { EmptyObject } from 'lib/object/objectUtils'
import useAllCharterFlightJourneys from './useAllCharterFlightJourneys'
import moment from 'moment'

export function extractDepartingAndReturningFaresForClass(fareFamilies: FareFamiliesResponse, fareClass: FlightsClassTypes): [App.FlightFareFamily | undefined, App.FlightFareFamily | undefined] {
  const apiCabinName = fareClassToApiCabinMap[fareClass]
  const departingFareFamily = fareFamilies.upsellFaresPerLeg?.[0]?.find(upsell => upsell.cabin === apiCabinName)
  const returningFareFamily = fareFamilies.upsellFaresPerLeg?.[1]?.find(upsell => upsell.cabin === apiCabinName)
  return [departingFareFamily, returningFareFamily]
}

export default function useCharterFlightJourneysForHotelDates({
  charterBundleRoute,
  checkInDate,
  checkOutDate,
  occupancies = EmptyArray,
  fareClass,
  disabled,
}: {
  charterBundleRoute?: App.CharterBundleRoute
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
  fareClass: FlightsClassTypes
  disabled?: boolean
}) {
  const { currentRegionCode } = useContext(GeoContext)

  // GET FLIGHTS
  const flightResults = useAllCharterFlightJourneys({
    charterBundleRoute,
    fareClass,
    occupancies,
    disabled,
  })

  const providerSearchId = flightResults?.searchId
  const searchResultsTimestamp = flightResults?.timestamp
  const journeysError = flightResults?.error
  const fetchingJourneys = flightResults?.fetching

  const departingJourney = checkInDate ? flightResults?.fares.at(0)?.find(journey => {
    const arrivalDate = journey.flightGroup.flights.at(-1)?.arrivalDate
    return arrivalDate && moment(arrivalDate).format('YYYY-MM-DD') === checkInDate
  }) : undefined

  const returningJourney = checkOutDate ? flightResults?.fares.at(1)?.find(journey => {
    const departingDate = journey.flightGroup.flights.at(0)?.departingDate
    return departingDate && moment(departingDate).format('YYYY-MM-DD') === checkOutDate
  }) : undefined

  // GET FARE FAMILIES
  const {
    data: { fareFamilies, departingFareFamily, returningFareFamily } = EmptyObject as { fareFamilies?: FareFamiliesResponse, departingFareFamily?: App.FlightFareFamily | undefined, returningFareFamily?: App.FlightFareFamily | undefined },
    status: fareFamiliesFetchStatus,
  } = useQuery<{ fareFamilies?: FareFamiliesResponse, departingFareFamily?: App.FlightFareFamily | undefined, returningFareFamily?: App.FlightFareFamily | undefined }>({
    queryFn: async() => {
      if (!providerSearchId || !departingJourney || !returningJourney) {
        return EmptyObject
      }

      const fareFamilies = await getFlightFareFamilies({
        searchId: providerSearchId,
        sessionId: '',
        origin: departingJourney.flightGroup.flights[0].departureAirport,
        destination: returningJourney.flightGroup.flights[0].departureAirport,
        occupancies: mergeOccupants(occupancies),
        region: currentRegionCode,
        isStandalone: false,
        selectedFareIds: [departingJourney.id, returningJourney.id],
      })

      const [departingFareFamily, returningFareFamily] = extractDepartingAndReturningFaresForClass(fareFamilies, fareClass)

      return { fareFamilies, departingFareFamily, returningFareFamily }
    },
    queryKey: [departingJourney?.flightGroup.flights[0].departureAirport, returningJourney?.flightGroup.flights[0].departureAirport, departingJourney?.id, returningJourney?.id, occupancies, currentRegionCode],
    enabled: !!(providerSearchId && departingJourney && returningJourney),
  })

  // If journey search is complete but no journeys found, we should not be fetching
  // However, if we have route and dates but no data yet, we should show loading (especially for SSR)
  const hasSearchCompleted = !!searchResultsTimestamp || !!journeysError
  const shouldBeFetching = !!charterBundleRoute && (
    fetchingJourneys ||
    (fareFamiliesFetchStatus === 'loading' && !!departingJourney && !!returningJourney) ||
    (!hasSearchCompleted && !departingJourney && !returningJourney && (!!checkInDate || !!checkOutDate))
  )

  return {
    departingJourney,
    returningJourney,
    fetching: shouldBeFetching,
    error: !!charterBundleRoute && (!!journeysError || fareFamiliesFetchStatus === 'error'),
    searchResultsTimestamp,
    providerSearchId,
    departingFareFamily,
    returningFareFamily,
    fareFamilies,
    hasAllFareData: !!(departingJourney && returningJourney && departingFareFamily && returningFareFamily)
  }
}
